// Strip /api suffix to get the media host (e.g. https://mlb.meralunchbox.in)
const _MEDIA_HOST = (import.meta.env.VITE_BASE_URL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");

/**
 * Resolves a media path to a full URL.
 * - Already a full URL  → returned as-is
 * - Contains "media/"  → domain prepended without adding /media/ again
 * - Plain relative path → domain + /media/ prepended
 */
export function getMediaUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const clean = path.replace(/^\//, "");
  if (clean.startsWith("media/")) return `${_MEDIA_HOST}/${clean}`;
  return `${_MEDIA_HOST}/media/${clean}`;
}

import {
  FetchHelperOptions,
  FetchResponse,
  QueryParams,
  ServiceError,
  ServiceErrorResult,
} from "./helper"
import { getCookie } from "./cookie";
import getHeaders from "./headers";
import { isFuture, isValid, sub } from "date-fns";
import storage from "./storage";
import { logoutService } from "@/services/auth";

const baseUrl = import.meta.env.VITE_BASE_URL;

export const buildApiPath = (apiPath: string): string => `${baseUrl}${apiPath}`;

const DEFAULT_ERROR_MESSAGE = "Network error or unknown issue occurred";
const STATUS_ERROR_MESSAGES: Record<number, string> = {
  400: "Bad request",
  401: "Unauthorized access - please log in",
  403: "Forbidden access",
  404: "Resource not found",
  500: "Server error",
};

let isLoggingOut = false;
let refreshPromise: Promise<string | null> | null = null;

async function handleLogout() {
  // prevent multiple logout calls
  if (isLoggingOut) return;

  isLoggingOut = true;

  try {
    const refreshToken = storage.getRefreshToken();

    if (refreshToken) {
      await logoutService(refreshToken);
    }
  } catch (err) {
    console.error("Logout API failed:", err);
  } finally {
    storage.logout();

    window.location.href = "/#/login";
  }
}

export const serviceErrorHelper = (error: ServiceError): ServiceErrorResult | undefined => {
  if (!error?.response) return undefined;

  const { data = {}, status, statusText } = error.response;

  // ✅ Fixed: Proper type guard for Object.keys
  if (
    status === 400 &&
    data &&
    typeof data === 'object' &&
    data !== null &&
    Object.keys(data as Record<string, any>).length > 0
  ) {
    return data as ServiceErrorResult;
  }

  return { non_field_errors: [statusText || 'Unknown error', "Please try later"] };
};

async function handleFetchError(response: Response): Promise<FetchResponse<null>> {
  // Never expose backend details for 5xx errors
  if (response.status > 500) {
    // await handleLogout(); 
    return {
      errors: { non_field_errors: ["Unexpected error. Unable to perform the action, please try again."] },
      status: response.status,
    };
  }

  let errors: any;

  try {
    const contentType = response.headers.get("Content-Type") || "";
    if (contentType.includes("application/json")) {
      errors = await response.json();
    } else {
      const errorText = await response.text();
      try {
        errors = JSON.parse(errorText);
      } catch {
        errors = { non_field_errors: [errorText] };
      }
    }
  } catch (error) {
    console.error("error: ", error);
    errors = {
      non_field_errors: ["Unexpected error occurred. Please try again later"],
    };
  }

  const defaultMessage = STATUS_ERROR_MESSAGES[response.status] || "An error occurred";

  // ✅ Fixed: Proper type guard before Object.keys
  if (
    errors &&
    typeof errors === "object" &&
    errors !== null &&
    Object.keys(errors as Record<string, any>).length > 0
  ) {
    if (errors.detail) {
      return {
        errors: { non_field_errors: [errors.detail] },
        status: response.status,
      };
    } else if (errors.non_field_errors) {
      return {
        errors: {
          non_field_errors: Array.isArray(errors.non_field_errors)
            ? errors.non_field_errors
            : [errors.non_field_errors],
        },
        status: response.status,
      };
    } else {
      return {
        errors: errors as ServiceErrorResult,
        status: response.status,
      };
    }
  }

  return {
    errors: { non_field_errors: [defaultMessage] },
    status: response.status,
  };
}

async function handleFetchResponse<T>(response: Response): Promise<FetchResponse<T>> {
  if (response.ok) {
    if (response.status === 204) {
      return { status: response.status };
    }

    try {
      const data = (await response.json()) as T;
      return { data, status: response.status };
    } catch {
      if (response.status === 200 || response.status === 201) {
        return {
          status: response.status,
          data: null as T,
          message: "API call successful",
        };
      }
      return {
        data: null as T,
        status: response.status,
        errors: { non_field_errors: ["Failed to parse response"] },
      };
    }
  }

  return (await handleFetchError(response)) as unknown as FetchResponse<T>;
}

export async function fetchHelper<T, U = Record<string, unknown>>(
  apiPath: string,
  options: FetchHelperOptions<U> = {},
): Promise<FetchResponse<T>> {
  const {
    method = "GET",
    data,
    isMultipart = false,
    includeToken = true,
    includeSessionKey = true,
    errorMessage,
    isExport = false,
    excludeRefresh = true
  } = options;
  try {
    let token = includeToken ? storage.getAuthToken() : "";
    const sessionKey = (await getCookie("sessionKey")) || "";

    let headers = getHeaders(token, sessionKey, isMultipart, includeSessionKey);

    const fetchPath = buildApiPath(apiPath);

    let fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (
  method !== "GET" &&
  method !== "DELETE" &&
  data
) {

  if (isMultipart) {

    fetchOptions.body =
      data instanceof FormData
        ? data
        : (() => {

            const form =
              new FormData();

            Object.entries(
              data as Record<
                string,
                any
              >
            ).forEach(
              ([key, value]) => {

                if (
                  value !== undefined &&
                  value !== null
                ) {

                  form.append(
                    key,
                    value
                  );
                }
              }
            );

            return form;
          })();

  } else {

    fetchOptions.body =
      JSON.stringify(data);
  }
}

    let response = await fetch(fetchPath, fetchOptions);

    const shouldSkipRefresh = apiPath === "/registeredcreate/" ||
      apiPath === "/refresh/";

    if (
      response.status === 401 &&
      includeToken &&
      !shouldSkipRefresh &&
      excludeRefresh
    ) {
      try {
        const refreshToken = storage.getRefreshToken();

        if (!refreshToken) {
          await handleLogout();

          return {
            errors: {
              non_field_errors: [
                "Session expired. Please login again.",
              ],
            },
            status: 401,
          };
        }

        // already refreshing → wait
        if (!refreshPromise) {
          refreshPromise = (async () => {
            const refreshResponse = await fetch(
              `${baseUrl}/refresh/`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  refresh: refreshToken,
                }),
              }
            );

            if (!refreshResponse.ok) {
              storage.logout();
              if (window.location.pathname !== "/login") {
                window.location.href = "/#/login";
              }

              return {
                errors: {
                  non_field_errors: ["Session expired. Please login again."]
                },
                status: 401,
              };
            }

            const refreshData =
              await refreshResponse.json();

            if (!refreshData?.access) {
              storage.logout();
              if (window.location.pathname !== "/login") {
                window.location.href = "/#/login";
              }
            }

            storage.setAuthToken(
              refreshData.access
            );

            return refreshData.access;
          })();

          refreshPromise.finally(() => {
            refreshPromise = null;
          });
        }

        const newAccessToken =
          await refreshPromise;

        if (!newAccessToken) {
          await handleLogout();

          return {
            errors: {
              non_field_errors: [
                "Session expired. Please login again.",
              ],
            },
            status: 401,
          };
        }

        // retry original request once only
        headers = getHeaders(
          newAccessToken,
          sessionKey,
          isMultipart,
          includeSessionKey
        );

        fetchOptions.headers = headers;

        response = await fetch(
          fetchPath,
          fetchOptions
        );

        // still unauthorized after retry
        if (response.status === 401) {
          storage.logout();
          if (window.location.pathname !== "/login") {
            window.location.href = "/#/login";
          }

          return {
            errors: {
              non_field_errors: [
                "Session expired. Please login again.",
              ],
            },
            status: 401,
          };
        }
      } catch (error) {
        console.error(
          "Refresh token failed:",
          error
        );

        await handleLogout();

        return {
          errors: {
            non_field_errors: [
              "Session expired. Please login again.",
            ],
          },
          status: 401,
        };
      }
    }

    if (isExport && response.ok) {
      const textData = await response.text();
      return { success: true, data: textData as unknown as T, status: response.status };
    }

    return await handleFetchResponse<T>(response);
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }

    return {
      errors: serviceErrorHelper(error as ServiceError) || {
        non_field_errors: [errorMessage || DEFAULT_ERROR_MESSAGE],
      },
      status: 0,
    };
  }
}

export function parseObjToQuery(queryObj?: QueryParams): string {
  if (queryObj && typeof queryObj === 'object' && Object.keys(queryObj as Record<string, any>).length) {
    const parsedQuery = Object.keys(queryObj as Record<string, any>).map((mapItem) => {
      if (!queryObj[mapItem as keyof QueryParams]) {
        return null;
      }
      return `&${mapItem}=${queryObj[mapItem as keyof QueryParams]}`;
    });
    return `?${parsedQuery.filter(Boolean).join("").slice(1)}`;
  }
  return "";
}

export function getParsedQueries(queryEntries: Iterable<[string, string]>): Record<string, string> {
  const newItem: Record<string, string> = {};

  for (const [key, value] of Array.from(queryEntries)) {
    newItem[key] = value;
  }

  return newItem;
}

export function matchQueryWithValues(
  query: Record<string, string>,
  values: Record<string, string>,
): boolean {
  return Object.entries(values).every(([key, value]) => {
    if (
      value === "" ||
      value === undefined ||
      value === null ||
      (typeof value === "object" &&
        value !== null &&
        Object.keys(value as Record<string, any>).length === 0)
    ) {
      return !(key in query);
    }

    if (typeof value === "boolean") {
      return query[key] === value;
    }
    if (typeof value === "object" && value !== null) {
      if (typeof query[key] !== "object" || query[key] === null) return false;
      return matchQueryWithValues(query[key] as Record<string, string>, value as Record<string, string>);
    }

    return query[key] === value;
  });
}

// ✅ FIXED PromiseErrorHelper - Main issue resolved
export const PromiseErrorHelper = (promiseResponse: FetchResponse<any>[]) => {
  const errorsArr = promiseResponse.filter((errItem) => errItem.errors);
  let newData: string[] = [];

  errorsArr.forEach(({ errors }) => {
    if (typeof errors === "string") {
      newData.push(errors);
    } else if (Array.isArray(errors)) {
      newData = [...newData, ...errors];
    } else if (errors && typeof errors === 'object' && errors !== null) {
      // ✅ Fixed: Proper type guard + null check
      Object.keys(errors as Record<string, any>).forEach((item) => {
        const errorValue = (errors as Record<string, any>)[item];
        if (Array.isArray(errorValue)) {
          newData = [...newData, ...errorValue];
        } else if (errorValue) {
          newData.push(String(errorValue));
        }
      });
    }
  });

  return newData;
};

export const gridCols = (config: Record<string, number>) => {
  const { sm = 1, md, lg } = config;

  const mdCols =
    Math.round(md || 0) || (lg <= 2 ? 1 : lg <= 4 ? 2 : lg <= 6 ? 3 : lg <= 8 ? 4 : lg <= 10 ? 5 : 6);
  const lgCols = lg || sm;

  return `grid-cols-${sm} sm:grid-cols-${sm} md:grid-cols-${mdCols} lg:grid-cols-${lgCols}`;
};

export function formatPhoneNumber(phoneNumber: string): string {
  if (!phoneNumber) {
    return "";
  }

  const cleaned = phoneNumber.replace(/\D/g, "");

  let formatted = cleaned;

  if (cleaned.length >= 12) {
    formatted = `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7, 12)}`;
  } else if (cleaned.length >= 7) {
    formatted = `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  } else if (cleaned.length >= 3) {
    formatted = `+91 ${cleaned.slice(2)}`;
  } else if (cleaned.length === 10) {
    formatted = `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5, 10)}`;
  } else if (cleaned.length >= 5) {
    formatted = `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  } else if (cleaned.length >= 1) {
    formatted = `+91 ${cleaned}`;
  }

  return formatted;
}

export function isValidDate(date: string | Date): boolean {
  const parsedDate = typeof date === "string" ? new Date(date) : date;
  return isValid(parsedDate);
}

export function isFutureDate(date: string | Date): boolean {
  const parsedDate = typeof date === "string" ? new Date(date) : date;
  return isValid(parsedDate) && isFuture(parsedDate);
}

export function getYearsAgoDate(years: number): Date {
  return sub(new Date(), { years });
}

export function encodeSvg(svgContent: string) {
  const encodedSvg = encodeURIComponent(svgContent);
  const dataUrl = `data:image/svg+xml,${encodedSvg}`;
  return dataUrl;
}

export function parseISOPreservingTime(value: string | Date | undefined): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;

  if (typeof value === "string") {
    if (value.endsWith("Z")) {
      const localTimeString = value.slice(0, -1);
      return new Date(localTimeString);
    }
    return new Date(value);
  }

  return undefined;
}

export function modifiedQuery(selected: any, dropdownData: any) {
  const data =
    dropdownData &&
    dropdownData.length &&
    dropdownData.filter(
      (filterItem: any) => filterItem.value == selected || filterItem.id == selected,
    );
  return data;
}

export const generateSlug = (text: string): string => {
  if (!text) return "";

  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

export const formatText = (text: string): string => {
  if (!text) return "";
  return text
    .toLocaleLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export function truncate(number: number, digits: number) {
  const multiplier = Math.pow(10, digits);
  return Math.trunc(number * multiplier) / multiplier;
}

export function formatNumberToPrice(value: number): string {
  return value.toLocaleString("en-IN", { style: "currency", currency: "INR" });
}

export function formatNumber(value: number): string {
  return value.toLocaleString("en-IN");
}

export function convertGraphAmount(amount: number): string {
  if (amount >= 10000000) {
    const amountInCrores = truncate(amount / 10000000, 2);
    return `${formatNumberToPrice(amountInCrores)} Cr`;
  } else if (amount >= 100000) {
    const amountInLakhs = truncate(amount / 100000, 2);
    return `${formatNumberToPrice(amountInLakhs)} L`;
  } else if (amount >= 1000) {
    const amountInThousands = truncate(amount / 1000, 2);
    return `${formatNumberToPrice(amountInThousands)} K`;
  } else {
    const truncatedAmount = truncate(amount, 2);
    return formatNumberToPrice(truncatedAmount);
  }
}

export function parseLogs(logString: string): { timestamp: string; message: string }[] {
  const regex = /\[(.*?)\]\s([\s\S]*?)(?=\s\[|$)/g;
  const entries: { timestamp: string; message: string }[] = [];

  let match;
  while ((match = regex.exec(logString)) !== null) {
    entries.push({
      timestamp: match[1],
      message: match[2].trim(),
    });
  }
  return entries;
}
